<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-smoke-trend-analysis">
    <nx-report-header title="烟雾趋势分析">
      <div class="kb-header-actions">
        <el-select v-model="filters.areaName" clearable size="small" placeholder="全部区域">
          <el-option v-for="item in areaOptions" :key="item" :label="item" :value="item" />
        </el-select>
        <el-select v-model="filters.name" clearable size="small" placeholder="全部监测类型">
          <el-option v-for="item in nameOptions" :key="item" :label="item" :value="item" />
        </el-select>
        <el-select v-model="filters.level" clearable size="small" placeholder="全部风险等级">
          <el-option label="黄色 · 正常" value="NORMAL" />
          <el-option label="橙色 · 预警" value="WARNING" />
          <el-option label="红色 · 报警" value="ALARM" />
          <el-option label="灰色 · 数据异常" value="INVALID" />
        </el-select>
        <span class="kb-toolbar-hint">最新采集：{{ summary.latestCollectTime || '—' }}</span>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-5">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">监测点位</div>
        <div class="kb-kpi-value">{{ summary.totalCount }}</div>
        <div class="kb-kpi-sub">最新设备数据</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">正常点位</div>
        <div class="kb-kpi-value is-normal">{{ summary.normalCount }}</div>
        <div class="kb-kpi-sub">低于报警值的 60%</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">预警点位</div>
        <div class="kb-kpi-value is-warn">{{ summary.warningCount }}</div>
        <div class="kb-kpi-sub">达到报警值的 60%</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">报警点位</div>
        <div class="kb-kpi-value is-danger">{{ summary.alarmCount }}</div>
        <div class="kb-kpi-sub">达到或超过报警值</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">数据异常</div>
        <div class="kb-kpi-value is-muted">{{ summary.invalidCount }}</div>
        <div class="kb-kpi-sub">无数值或无报警阈值</div>
      </div>
    </div>

    <div class="kb-section smoke-legend-section">
      <div class="smoke-legend">
        <span><i class="dot normal" />黄色：正常</span>
        <span><i class="dot warning" />橙色：预警</span>
        <span><i class="dot alarm" />红色闪烁：报警</span>
        <span><i class="dot invalid" />灰色：数据异常</span>
        <span class="legend-tip">点击设备位置查看详细信息</span>
      </div>
    </div>

    <div v-if="filteredAreas.length" class="area-list">
      <section v-for="area in filteredAreas" :key="area.areaName" class="kb-section smoke-area-section">
        <header class="kb-section-head">
          <div class="area-title">
            <i class="el-icon-location-outline" />
            <span>{{ area.areaName }}</span>
          </div>
          <small>{{ area.devices.length }} 个监测点</small>
        </header>
        <div class="device-grid">
          <button v-for="device in area.devices" :key="device.code" type="button" class="device-card" :class="levelClass(device.level)" @click="showDetail(device, area.areaName)">
            <span class="risk-badge">{{ levelText(device.level) }}</span><span class="device-type">{{ device.name || '未知类型' }}</span>
            <strong class="device-value">{{ valueText(device) }} <em>{{ device.unit || '' }}</em></strong><span class="device-location" :title="device.location">{{ device.location }}</span>
            <span class="device-meta"><span>{{ device.code }}</span><span>阈值 {{ thresholdText(device) }}</span></span>
          </button>
        </div>
      </section>
    </div>
    <el-empty v-else-if="!loading" description="暂无符合条件的环境监测数据" />
    <el-dialog title="监测点详细信息" :visible.sync="detailVisible" width="540px">
      <div v-if="activeDevice" class="detail-body">
        <div class="detail-level" :class="levelClass(activeDevice.level)"><span>{{ levelText(activeDevice.level) }}</span><strong>{{ valueText(activeDevice) }} {{ activeDevice.unit || '' }}</strong></div>
        <el-descriptions :column="1" border size="medium"><el-descriptions-item label="设备编码">{{ activeDevice.code || '—' }}</el-descriptions-item><el-descriptions-item label="监测类型">{{ activeDevice.name || '—' }}</el-descriptions-item><el-descriptions-item label="设备区域">{{ activeAreaName || '—' }}</el-descriptions-item><el-descriptions-item label="设备位置">{{ activeDevice.location || '—' }}</el-descriptions-item><el-descriptions-item label="报警阈值">{{ thresholdText(activeDevice) }}</el-descriptions-item><el-descriptions-item label="采集时间">{{ activeDevice.collectTime || '—' }}</el-descriptions-item><el-descriptions-item label="报警时间">{{ activeDevice.alarmTime || '未触发报警' }}</el-descriptions-item><el-descriptions-item label="工作状态">{{ activeDevice.workStatus || '—' }}</el-descriptions-item></el-descriptions>
      </div><span slot="footer"><el-button type="primary" @click="detailVisible = false">关闭</el-button></span>
    </el-dialog>
  </div>
</template>
<script>
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchSmokeTrendAnalysis } from './api'
const REFRESH_INTERVAL = 60000
export default {
  name: 'KanbanSmokeTrendAnalysis',
  components: { NxReportHeader },
  data() { return { loading: false, areas: [], summary: { latestCollectTime: '', totalCount: 0, normalCount: 0, warningCount: 0, alarmCount: 0, invalidCount: 0 }, filters: { areaName: '', name: '', level: '' }, detailVisible: false, activeDevice: null, activeAreaName: '', refreshTimer: null } },
  computed: {
    areaOptions() { return this.areas.map((item) => item.areaName).filter(Boolean) },
    nameOptions() { const values = []; this.areas.forEach((area) => { (area.devices || []).forEach((device) => { if (device.name && values.indexOf(device.name) < 0) values.push(device.name) }) }); return values },
    filteredAreas() { return this.areas.filter((area) => !this.filters.areaName || area.areaName === this.filters.areaName).map((area) => ({ areaName: area.areaName, devices: (area.devices || []).filter((device) => (!this.filters.name || device.name === this.filters.name) && (!this.filters.level || device.level === this.filters.level)) })).filter((area) => area.devices.length) }
  },
  mounted() { this.loadData(); this.refreshTimer = window.setInterval(this.loadData, REFRESH_INTERVAL) },
  beforeDestroy() { if (this.refreshTimer) window.clearInterval(this.refreshTimer) },
  methods: {
    async loadData() { this.loading = true; try { const raw = (await fetchSmokeTrendAnalysis({})) || {}; this.areas = raw.areas || []; this.summary = { latestCollectTime: raw.latestCollectTime || '', totalCount: raw.totalCount || 0, normalCount: raw.normalCount || 0, warningCount: raw.warningCount || 0, alarmCount: raw.alarmCount || 0, invalidCount: raw.invalidCount || 0 } } catch (error) { this.areas = []; this.$message.error((error && error.message) || '环境监测数据加载失败') } finally { this.loading = false } },
    levelClass(level) { return 'level-' + String(level || 'INVALID').toLowerCase() },
    levelText(level) { return { NORMAL: '正常', WARNING: '预警', ALARM: '报警', INVALID: '数据异常' }[level] || '数据异常' },
    valueText(device) { return device && device.value != null ? device.value : device && device.rawValue ? device.rawValue : '—' },
    thresholdText(device) { return !device || device.alarmValue == null ? '—' : device.alarmValue + ' ' + (device.unit || '') },
    showDetail(device, areaName) { this.activeDevice = device; this.activeAreaName = areaName; this.detailVisible = true }
  }
}
</script>
<style lang="scss" scoped>
@import './index.scss';
</style>
